import { useMemo, useRef, useState } from "react";
import { UploadCloud, Loader2, Download, ScanSearch, X, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORY_LABELS = {
  diameter: "Diameter",
  radius: "Radius",
  angle: "Angle",
  length: "Length",
  gdt: "GD&T",
  surface_finish: "Surface finish",
  zone_note: "Zone note",
  edge_note: "Edge note",
  performance_spec: "Performance spec",
};

// Vision extraction on a dense drawing takes well over the shared 45s client default.
const EXTRACT_TIMEOUT_MS = 200000;

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function DrawingExtract() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showReference, setShowReference] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // A PDF has no browser-renderable preview; the pipeline renders page 1 server-side.
  const isPdf = /\.pdf$/i.test(file?.name || "") || file?.type === "application/pdf";
  const fileRef = useRef(null);

  const rows = useMemo(() => {
    const items = result?.items || [];
    return showReference ? items : items.filter((i) => !i.is_reference);
  }, [result, showReference]);

  const referenceCount = (result?.items || []).filter((i) => i.is_reference).length;
  const lowCount = (result?.items || []).filter((i) => i.confidence === "low").length;

const DRAWING_EXTS = ["pdf", "png", "jpg", "jpeg", "tif", "tiff", "bmp"];

  function pickFile(f) {
    if (!f) return;
    // Decide by EXTENSION, never by MIME: Windows browsers send "" or
    // application/octet-stream for .tif.
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!DRAWING_EXTS.includes(ext)) {
      toast.error(`Unsupported file type ".${ext}" — upload ${DRAWING_EXTS.map((e) => `.${e}`).join(" ")}`);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
  }

  function clearAll() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
    setResult(null);
  }

  async function extract() {
    if (!file || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/extract-drawing", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: EXTRACT_TIMEOUT_MS,
      });
      setResult(r.data);
      const n = r.data?.total_count || 0;
      toast[n ? "success" : "warning"](
        n ? `${n} key characteristic${n === 1 ? "" : "s"} found` : "No leader-linked callouts found",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setLoading(false);
    }
  }

  function exportCsv() {
    if (!rows.length) return;
    const header = ["ID", "Extracted Text", "Category", "Location Hint", "Line Evidence", "Reference", "Confidence"];
    const body = rows.map((i) => [
      i.id,
      i.extracted_text,
      CATEGORY_LABELS[i.category] || i.category,
      i.location_hint,
      i.line_description,
      i.is_reference ? "Yes" : "No",
      i.confidence,
    ]);
    // BOM so Excel reads Ø / ± / ° correctly.
    const csv = "﻿" + [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(file?.name || "drawing").replace(/\.[^.]+$/, "")}-key-characteristics.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-full bg-slate-50" data-testid="drawing-extract-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-slate-700 mb-1">
          Drawing <span className="text-indigo-600">Key-Characteristic</span> Extractor
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Upload a drawing image — every callout connected to the part by a leader line is read out into a table.
        </p>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upload + preview */}
          <div>
            <div
              onClick={() => { if (!loading) fileRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!loading) pickFile(e.dataTransfer.files?.[0]); }}
              className={`border-2 border-dashed rounded-xl px-6 py-8 text-center transition ${
                loading ? "border-indigo-300 bg-indigo-50 cursor-wait"
                  : dragOver ? "cursor-pointer border-indigo-400 bg-indigo-50"
                  : "cursor-pointer border-slate-300 bg-white hover:bg-slate-50"
              }`}
              data-testid="drawing-extract-dropzone"
            >
              <UploadCloud size={28} className="mx-auto text-indigo-500 mb-2" />
              <div className="text-slate-600">
                <span className="text-indigo-600 font-semibold">Click here</span>, or drag and drop.
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                PDF, PNG, JPG, TIF or BMP · max 25 MB — a PDF gives the best results
              </div>
              <input
                ref={fileRef} type="file" accept={DRAWING_EXTS.map((e) => `.${e}`).join(",")} className="hidden"
                onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="drawing-extract-file-input"
              />
            </div>

            {previewUrl && (
              <div className="mt-4 bg-white border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-xs font-medium text-slate-600 truncate flex-1">{file?.name}</div>
                  <button onClick={clearAll} className="text-slate-400 hover:text-red-600 p-1" title="Remove">
                    <X size={15} />
                  </button>
                </div>
                {isPdf ? (
                  <div className="w-full h-[180px] flex flex-col items-center justify-center gap-2 rounded bg-slate-50 border border-slate-200 text-slate-500">
                    <FileText size={30} className="text-indigo-500" />
                    <div className="text-xs">PDF ready — page 1 will be read at high resolution</div>
                  </div>
                ) : (
                  <img src={previewUrl} alt="Drawing preview" className="w-full max-h-[420px] object-contain rounded bg-slate-50" />
                )}
                <button
                  onClick={extract} disabled={loading}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md px-4 py-2"
                  data-testid="drawing-extract-run"
                >
                  {loading ? <><Loader2 size={15} className="animate-spin" /> Reading the drawing…</>
                    : <><ScanSearch size={15} /> Extract key characteristics</>}
                </button>
                {loading && (
                  <div className="text-[11px] text-slate-500 mt-2 text-center">
                    Detecting leader lines, then reading each one — this usually takes 20–60 seconds.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Results */}
          <div>
            {!result ? (
              <div className="h-full min-h-[260px] flex items-center justify-center text-sm text-slate-400 border border-slate-200 rounded-lg bg-white text-center px-6">
                {loading ? "Extracting…" : "Results will appear here once you run an extraction."}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-lg">
                {/* Sticky: the count must stay on screen while the table scrolls. It sat in a
                    flex-wrap row that pushed it onto its own line, so on a narrow results
                    panel it scrolled out of view and the table appeared to have no total. */}
                <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 p-3 border-b border-slate-200 bg-white">
                  {/* SINGLE SOURCE OF TRUTH: this badge counts `rows` — the exact array the
                      table renders and exportCsv() writes. It previously read
                      result.total_count, which counts items BEFORE the reference-dimension
                      filter below, so the badge said 14 while the CSV held 10. Any hidden
                      rows are now stated explicitly rather than silently dropped. */}
                  <div className="text-sm font-semibold text-slate-800" data-testid="drawing-extract-total">
                    Total: <span className="text-indigo-600 text-base">{rows.length}</span>
                    {rows.length !== (result.total_count || 0) && (
                      <span className="ml-1 font-normal text-slate-500">
                        (+{result.total_count - rows.length} ref hidden)
                      </span>
                    )}
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox" checked={showReference}
                      onChange={(e) => setShowReference(e.target.checked)}
                      className="accent-indigo-600"
                      data-testid="drawing-extract-show-reference"
                    />
                    Show reference dimensions{referenceCount ? ` (${referenceCount})` : ""}
                  </label>
                  <button
                    onClick={exportCsv} disabled={!rows.length}
                    className="ml-auto inline-flex items-center gap-1.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-40 text-slate-700 text-xs font-semibold rounded-md px-3 py-1.5"
                    data-testid="drawing-extract-export"
                  >
                    <Download size={13} /> Export to Excel/CSV
                  </button>
                </div>

                {/* Provenance: which pipeline ran and what the CV stage saw. Without this a
                    thin table is indistinguishable from "the detector found no lines". */}
                <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                  {result.pipeline === "line-crop" ? (
                    <span>
                      <b className="text-slate-700">Line-crop pipeline</b> — {result.line_count} leader
                      line{result.line_count === 1 ? "" : "s"} detected, read in {result.crop_count} crop
                      {result.crop_count === 1 ? "" : "s"}
                      {result.review_crop_count > 0 &&
                        ` (${result.review_crop_count} of uncertain shape, marked “cv-only”)`}
                    </span>
                  ) : (
                    <span>
                      <b className="text-slate-700">Whole-image pass</b> —{" "}
                      {result.pipeline === "whole-image-fallback"
                        ? "no leader lines were detected, so the model read the full sheet"
                        : "line detection unavailable, so the model read the full sheet"}
                    </span>
                  )}
                  {Math.abs(result.deskew_angle || 0) >= 0.1 && (
                    <span>· deskewed {result.deskew_angle > 0 ? "+" : ""}{result.deskew_angle}°</span>
                  )}
                  {result.ambiguous_count > 0 && (
                    <span className="text-orange-700">
                      · {result.ambiguous_count} line{result.ambiguous_count === 1 ? "" : "s"} held
                      back: the crop caught more than one value, so no reading is trusted
                    </span>
                  )}
                  {result.failed_crop_count > 0 && (
                    <span className="text-amber-700">
                      · {result.failed_crop_count} crop{result.failed_crop_count === 1 ? "" : "s"} could
                      not be read — some callouts may be missing
                    </span>
                  )}
                </div>

                {/* Second-opinion audit. A full-sheet vision pass runs alongside the CV
                    pipeline; it is NOT the detector (it is the approach this pipeline
                    replaced) but its failure mode is different, so anything only IT found
                    points at a crop/detection gap worth a human glance. */}
                {result.audit?.available && result.audit.audit_pass_only?.length > 0 && (
                  <details className="border-b border-orange-200 bg-orange-50" data-testid="drawing-extract-audit">
                    <summary className="px-3 py-2 text-[11px] text-orange-800 cursor-pointer select-none">
                      A second, independent read of the sheet found{" "}
                      <b>{result.audit.audit_pass_only.length}</b> value
                      {result.audit.audit_pass_only.length === 1 ? "" : "s"} this table does not —
                      worth checking against the drawing
                      <span className="text-orange-600">
                        {" "}({result.audit.summary.confirmed_count} of {result.audit.summary.primary_count} rows
                        confirmed by both methods)
                      </span>
                    </summary>
                    <ul className="px-3 pb-2 space-y-1">
                      {result.audit.audit_pass_only.map((a, n) => (
                        <li key={n} className="text-[11px] text-orange-900">
                          <span className="font-semibold">{a.extracted_text}</span>
                          {a.category ? <span className="text-orange-700"> · {CATEGORY_LABELS[a.category] || a.category}</span> : null}
                          {a.is_reference ? <span className="text-orange-600"> · reference</span> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {result.filtered_count > 0 && (
                  <details className="border-b border-slate-200 bg-slate-50" data-testid="drawing-extract-filtered">
                    <summary className="px-3 py-2 text-[11px] text-slate-600 cursor-pointer select-none">
                      Safety net removed <b>{result.filtered_count}</b> of {result.raw_model_count} model item(s) —
                      click to review
                    </summary>
                    <ul className="px-3 pb-2 space-y-1">
                      {result.filtered_items?.map((f, n) => (
                        <li key={n} className="text-[11px] text-slate-500">
                          <span className="text-slate-700 font-medium">{f.text}</span>
                          <span className="text-slate-400"> — {f.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {lowCount > 0 && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-800">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    {lowCount} item{lowCount === 1 ? " is" : "s are"} low-confidence (highlighted below) — check
                    {lowCount === 1 ? " it" : " them"} against the drawing before use.
                  </div>
                )}

                <div className="max-h-[520px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                        <th className="px-3 py-2 font-semibold">ID</th>
                        <th className="px-3 py-2 font-semibold">Extracted text</th>
                        <th className="px-3 py-2 font-semibold">Category</th>
                        <th className="px-3 py-2 font-semibold">Location hint</th>
                        <th className="px-3 py-2 font-semibold">Line evidence</th>
                        <th className="px-3 py-2 font-semibold">Ref?</th>
                        <th className="px-3 py-2 font-semibold">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                            {result.total_count
                              ? "All items are reference dimensions — tick “Show reference dimensions”."
                              : "No leader-linked callouts were found in this drawing."}
                          </td>
                        </tr>
                      ) : rows.map((i) => (
                        <tr
                          key={i.id}
                          className={`border-t border-slate-100 ${i.confidence === "low" ? "bg-yellow-50" : ""}`}
                          data-testid={`drawing-extract-row-${i.id}`}
                        >
                          <td className="px-3 py-2 text-slate-400 tabular-nums">{i.id}</td>
                          <td className="px-3 py-2 font-medium text-slate-800">{i.extracted_text}</td>
                          <td className="px-3 py-2 text-slate-600">{CATEGORY_LABELS[i.category] || i.category}</td>
                          <td className="px-3 py-2 text-slate-500">{i.location_hint || "—"}</td>
                          <td className="px-3 py-2 text-slate-400 text-[11px] max-w-[220px]">{i.line_description || "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{i.is_reference ? "Yes" : "No"}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${
                              i.confidence === "high" ? "bg-emerald-100 text-emerald-700"
                                : i.confidence === "medium" ? "bg-slate-100 text-slate-600"
                                : "bg-yellow-200 text-yellow-900"
                            }`}>
                              {i.confidence}
                            </span>
                            {/* The line detector was unsure this was a leader at all. The
                                text may be read correctly — the doubt is about the line — so
                                the row is kept and flagged rather than dropped. */}
                            {i.needs_review && (
                              <span
                                className="ml-1 text-[11px] font-semibold rounded px-1.5 py-0.5 bg-orange-100 text-orange-800"
                                title="The detected line is an unusual shape for a leader — confirm against the drawing."
                              >
                                cv-only
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
